import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MainLayout } from './layouts/MainLayout';
import { EmptyLayout } from './layouts/EmptyLayout';
import LocalStorage from './plugins/localStorage';
import './App.css'
import { Spin } from 'antd';

// Ленивые загрузки страниц
const Login = React.lazy(() => import('./pages/auth/Login').then(module => ({ default: module.Login })));
const NotFoundPage = React.lazy(() => import('./pages/not-found/404').then(module => ({ default: module.NotFoundPage })));
const MainPage = React.lazy(() => import('./pages/index').then(module => ({ default: module.MainPage })));
const UserPage = React.lazy(() => import('./pages/user/index').then(module => ({ default: module.UserPage })));
const UserEditPage = React.lazy(() => import('./pages/user/page').then(module => ({ default: module.UserEditPage })));
const UserProfile = React.lazy(() => import('./pages/user/profile').then(module => ({ default: module.UserProfile })));
const OfficesPage = React.lazy(() => import('./pages/office/index').then(module => ({ default: module.OfficesPage })));
const OfficeEditPage = React.lazy(() => import('./pages/office/page').then(module => ({ default: module.OfficeEditPage })));
const UserStatusPage = React.lazy(() => import('./pages/user-status/index').then(module => ({ default: module.UserStatusPage })));
const UserStatusEditPage = React.lazy(() => import('./pages/user-status/page').then(module => ({ default: module.UserStatusEditPage })));
const UserTypePage = React.lazy(() => import('./pages/user-type/index').then(module => ({ default: module.UserTypePage })));
const UserTypeEditPage = React.lazy(() => import('./pages/user-type/page').then(module => ({ default: module.UserTypeEditPage })));
const ClientSourcePage = React.lazy(() => import('./pages/client-source/index').then(module => ({ default: module.ClientSourcePage })));
const ClientSourceEditPage = React.lazy(() => import('./pages/client-source/page').then(module => ({ default: module.ClientSourceEditPage })));
const ContractPage = React.lazy(() => import('./pages/contract/index').then(module => ({ default: module.ContractPage })));
const ContractEditPage = React.lazy(() => import('./pages/contract/page').then(module => ({ default: module.ContractEditPage })));
const ClientsPage = React.lazy(() => import('./pages/client/index').then(module => ({ default: module.ClientsPage })));
const ClientsEditPage = React.lazy(() => import('./pages/client/page').then(module => ({ default: module.ClientsEditPage })));
const RegionPage = React.lazy(() => import('./pages/region/index').then(module => ({ default: module.RegionPage })));
const RegionEditPage = React.lazy(() => import('./pages/region/page').then(module => ({ default: module.RegionEditPage })));
const CountryPage = React.lazy(() => import('./pages/country/index').then(module => ({ default: module.CountryPage })));
const CountryEditPage = React.lazy(() => import('./pages/country/page').then(module => ({ default: module.CountryEditPage })));
const CityPage = React.lazy(() => import('./pages/city/index').then(module => ({ default: module.CityPage })));
const CityEditPage = React.lazy(() => import('./pages/city/page').then(module => ({ default: module.CityEditPage })));
const RolesPage = React.lazy(() => import('./pages/role/index').then(module => ({ default: module.RolesPage })));
const RolesEditPage = React.lazy(() => import('./pages/role/page').then(module => ({ default: module.RolesEditPage })));
const CurrencyPage = React.lazy(() => import('./pages/currency/index').then(module => ({ default: module.CurrencyPage })));
const CurrencyEditPage = React.lazy(() => import('./pages/currency/page').then(module => ({ default: module.CurrencyEditPage })));
const WorkplacePage = React.lazy(() => import('./pages/workplace/index').then(module => ({ default: module.WorkplacePage })));
const WorkplaceEditPage = React.lazy(() => import('./pages/workplace/page').then(module => ({ default: module.WorkplaceEditPage })));
const ChatPage = React.lazy(() => import('./pages/chat/index').then(module => ({ default: module.ChatPage })));
const ChatEditPage = React.lazy(() => import('./pages/chat/page').then(module => ({ default: module.ChatEditPage })));
const AssignmentPage = React.lazy(() => import('./pages/assignment/index').then(module => ({ default: module.AssignmentPage })));
const AssignmentEditPage = React.lazy(() => import('./pages/assignment/page').then(module => ({ default: module.AssignmentEditPage })));
const ApplicationPage = React.lazy(() => import('./pages/application/index').then(module => ({ default: module.ApplicationPage })));
const ApplicationEditPage = React.lazy(() => import('./pages/application/page').then(module => ({ default: module.ApplicationEditPage })));
const SysSettingPage = React.lazy(() => import('./pages/sys-setting/index').then(module => ({ default: module.SysSettingPage })));
const SysSettingEditPage = React.lazy(() => import('./pages/sys-setting/page').then(module => ({ default: module.SysSettingEditPage })));
const BankPage = React.lazy(() => import('./pages/bank/index').then(module => ({ default: module.BanksPage })));
const BankEditPage = React.lazy(() => import('./pages/bank/page').then(module => ({ default: module.BankEditPage })));
const ApplicationStatusPage = React.lazy(() => import('./pages/application-status/index').then(module => ({ default: module.ApplicationStatusPage })));
const ApplicationStatusEditPage = React.lazy(() => import('./pages/application-status/page').then(module => ({ default: module.ApplicationStatusEditPage })));

// Добавьте ленивые загрузки для остальных страниц аналогично

const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const access_token = LocalStorage.get('access_token');
  const user = LocalStorage.get('user');

  if (!user || !access_token) {
    return <Navigate to={`/login?redirect=${window.location.pathname}`} replace />;
  }

  return children;
};

const AppRouter: React.FC = () => {
  return (
    <Router>
      <Suspense fallback={<div className="loading-overlay">
        <Spin spinning={true} size="large" />
      </div>}>
        <Routes>
          {/* Маршруты с основным layout */}
          <Route element={<MainLayout />}>
            <Route path="/" element={<ProtectedRoute><MainPage /></ProtectedRoute>} />
            <Route path="/user">
              <Route index element={<ProtectedRoute><UserPage /></ProtectedRoute>} />
              <Route path="page" element={<ProtectedRoute><UserEditPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><UserEditPage /></ProtectedRoute>} />
              <Route path="profile/:id" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
            </Route>
            <Route path='/office'>
              <Route index element={<ProtectedRoute><OfficesPage /></ProtectedRoute>} />
              <Route path="page" element={<ProtectedRoute><OfficeEditPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><OfficeEditPage /></ProtectedRoute>} />
            </Route>
            <Route path='/application-status'>
              <Route index element={<ProtectedRoute><ApplicationStatusPage /></ProtectedRoute>} />
              <Route path="page" element={<ProtectedRoute><ApplicationStatusEditPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><ApplicationStatusEditPage /></ProtectedRoute>} />
            </Route>
            <Route path='/user-status'>
              <Route index element={<ProtectedRoute><UserStatusPage /></ProtectedRoute>} />
              <Route path="page" element={<ProtectedRoute><UserStatusEditPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><UserStatusEditPage /></ProtectedRoute>} />
            </Route>
            <Route path='/user-type'>
              <Route index element={<ProtectedRoute><UserTypePage /></ProtectedRoute>} />
              <Route path="page" element={<ProtectedRoute><UserTypeEditPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><UserTypeEditPage /></ProtectedRoute>} />
              </Route>
            <Route path='/client-source'>
              <Route index element={<ProtectedRoute><ClientSourcePage /></ProtectedRoute>} />
              <Route path="page" element={<ProtectedRoute><ClientSourceEditPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><ClientSourceEditPage /></ProtectedRoute>} />
            </Route>
            <Route path='/contract'>
              <Route index element={<ProtectedRoute><ContractPage /></ProtectedRoute>} />
              <Route path="page" element={<ProtectedRoute><ContractEditPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><ContractEditPage /></ProtectedRoute>} />
            </Route>
            <Route path='/client'>
              <Route index element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
              <Route path="page" element={<ProtectedRoute><ClientsEditPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><ClientsEditPage /></ProtectedRoute>} />
            </Route>
            <Route path='/region'>
              <Route index element={<ProtectedRoute><RegionPage /></ProtectedRoute>} />
              <Route path="page" element={<ProtectedRoute><RegionEditPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><RegionEditPage /></ProtectedRoute>} />
            </Route>
            <Route path='/country'>
              <Route index element={<ProtectedRoute><CountryPage /></ProtectedRoute>} />
              <Route path="page" element={<ProtectedRoute><CountryEditPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><CountryEditPage /></ProtectedRoute>} />
            </Route>
            <Route path='/city'>
              <Route index element={<ProtectedRoute><CityPage /></ProtectedRoute>} />
              <Route path="page" element={<ProtectedRoute><CityEditPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><CityEditPage /></ProtectedRoute>} />
            </Route>
            <Route path='/role'>
              <Route index element={<ProtectedRoute><RolesPage /></ProtectedRoute>} />
              <Route path="page" element={<ProtectedRoute><RolesEditPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><RolesEditPage /></ProtectedRoute>} />
            </Route>
            <Route path='/currency'>
              <Route index element={<ProtectedRoute><CurrencyPage /></ProtectedRoute>} />
              <Route path="page" element={<ProtectedRoute><CurrencyEditPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><CurrencyEditPage /></ProtectedRoute>} />
            </Route>
            <Route path='/workplace'>
              <Route index element={<ProtectedRoute><WorkplacePage /></ProtectedRoute>} />
              <Route path="page" element={<ProtectedRoute><WorkplaceEditPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><WorkplaceEditPage /></ProtectedRoute>} />
            </Route>
            <Route path='/chat'>
              <Route index element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><ChatEditPage /></ProtectedRoute>} />
            </Route>
            <Route path='/assignment'>
              <Route index element={<ProtectedRoute><AssignmentPage /></ProtectedRoute>} />
              <Route path="page" element={<ProtectedRoute><AssignmentEditPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><AssignmentEditPage /></ProtectedRoute>} />
            </Route>
            <Route path='/application'>
              <Route index element={<ProtectedRoute><ApplicationPage /></ProtectedRoute>} />
              <Route path="page" element={<ProtectedRoute><ApplicationEditPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><ApplicationEditPage /></ProtectedRoute>} />
            </Route>
            <Route path='/sysSetting'>
              <Route index element={<ProtectedRoute><SysSettingPage /></ProtectedRoute>} />
              <Route path="page" element={<ProtectedRoute><SysSettingEditPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><SysSettingEditPage /></ProtectedRoute>} />
            </Route>
            <Route path='/bank'>
              <Route index element={<ProtectedRoute><BankPage /></ProtectedRoute>} />
              <Route path="page" element={<ProtectedRoute><BankEditPage /></ProtectedRoute>} />
              <Route path="page/:id" element={<ProtectedRoute><BankEditPage /></ProtectedRoute>} />
            </Route>
            {/* Добавьте остальные маршруты по аналогии */}
          </Route>
        

          {/* Маршруты с пустым layout */}
          <Route element={<EmptyLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRouter;
